import { createContext, useContext, useMemo } from "react";
import { resolveInheritedDataSet } from "./inheritDataSetResolve";

export type DataSetInheritContextValue = {
  /**
   * 要继承的数据集“基础名”，不包含 sessionId 前缀（例如：`items`）。
   * 会由消费组件再调用 `getDataSetStoreKey(sessionId, dataSet)` 拼接。
   */
  dataSet: string;
  /**
   * 可选业务标识（如动态容器槽位行 id），由 `inheritId` 或 store props 的 `id` 写入。
   * clickAction / changeAction 中可用占位符 `@inheritId` 读取。
   */
  id?: string | number;
  /** 迭代器行下标；对象上下文下可为空 */
  index?: number;
  /** 父层迭代器行下标（若存在） */
  parentIndex?: number;
  /** 父层上下文（用于多层嵌套扩展） */
  parentContext?: DataSetInheritContextValue;
  /** 可选：用于无 sessionId prop 时推导 store key */
  sessionId?: string;
  /**
   * 可选：数据字段根路径（不含数组下标）
   * 例如在 AndMasonry 场景下，masonry 自己的 dataSetFieldRoot 可能是 `value.data`，
   * 子组件的 dataSetField（如 `data`）会被解析为 `value.data[${index}].data`。
   */
  dataSetFieldRoot?: string;
};

/**
 * 给动态容器内部组件提供数据绑定上下文：
 * - 子组件不必显式传 `dataSet`
 * - 子组件可以仅声明 `dataSetField`，我们自动把它前缀成 `[index].*`
 */
export const DataSetInheritContext = createContext<
  DataSetInheritContextValue | null
>(null);

export { resolveInheritedDataSet } from "./inheritDataSetResolve";
export type { ResolvedInheritedDataSet, InheritContextForResolve } from "./inheritDataSetResolve";

/**
 * 当组件显式传了 `dataSet` 时，优先使用显式值；
 * 否则从 `DataSetInheritContext` 继承，并把 `dataSetField` 前缀成 `[index].*`。
 */
export function useResolvedDataSet(
  propsDataSet: string | undefined,
  propsDataSetField: string | undefined,
  propsSessionId: string | undefined
): {
  dataSet: string | undefined;
  dataSetField: string | undefined;
  sessionId: string | undefined;
} {
  const ctx = useContext(DataSetInheritContext);

  return useMemo(
    () =>
      resolveInheritedDataSet(propsDataSet, propsDataSetField, propsSessionId, ctx),
    [ctx, propsDataSet, propsDataSetField, propsSessionId]
  );
}
